import { BASE_CURRENCY, ENDPIONTS, httpService, Operation, PaginatedResult } from "@api";
import { useFetch } from "@hooks";
import { Branch, Payroll } from "@models";
import { Action, ComplexHeader, Filterable, Table, TableVerticalConfigs } from "@shared";
import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { FiPlus } from "react-icons/fi";
import { PAYROLL_STATUS_LABELS, PayrollAwaitingVM } from "@viewModels";
import PayrollStageTracker from "./PayrollStageTracker";
import PayrollApprovalPanel from "./PayrollApprovalPanel";
import { confirmAdvancePayroll, confirmReturnPayroll } from "./payrollWorkflowDialogs";

const ListPayroll = () => {
  const [queryStrings] = useSearchParams();
  const MySwal = withReactContent(Swal);
  const navigate = useNavigate();

  const [reload, setReload] = useState<boolean | undefined>(undefined);

  const fetchPayrolls = useFetch<PaginatedResult<Payroll>>(
    {
      endPoint: ENDPIONTS.payrolls,
      queryStrings: queryStrings,
      reload: reload,
    },
    new PaginatedResult<Payroll>()
  );

  const fetchBranches = useFetch<PaginatedResult<Branch>>(
    {
      endPoint: ENDPIONTS.branches,
      reload: reload,
      ignorePagination: true,
    },
    new PaginatedResult<Branch>()
  );

  // Payrolls waiting for the signed-in user's decision.
  const [awaiting, setAwaiting] = useState<PayrollAwaitingVM[]>([]);
  useEffect(() => {
    (async () => {
      const res = await httpService(ENDPIONTS.payrollsAwaitingMyAction).getAll();
      if (res?.status === 200) setAwaiting(res.data ?? []);
    })();
  }, [reload]);

  const refresh = () => setReload((prev) => (prev === undefined ? true : !prev));
  const payrollRef = (d: Payroll) => ({ id: d.id, title: `${d.branch?.location ?? ""} - ${d.forMonth}`, actions: d.actions! });

  const showApprovals = (id: number) =>
    MySwal.fire({
      showConfirmButton: false,
      showCloseButton: true,
      width: 900,
      html: <PayrollApprovalPanel payrollId={id} onChanged={refresh} />,
    });

  const headers: ComplexHeader[] = [
    { key: "branch?.location", title: "branch" },
    { key: "forMonth", title: "for month" },
    { key: "totalEmployees", title: "total Employees" },
    {
      key: "totalRecurringDeductions",
      title: "deductions",
      format: "currency",
      currency: () => BASE_CURRENCY,
    },
    {
      key: "totalLoans",
      title: "loans",
      format: "currency",
      currency: () => BASE_CURRENCY,
    },
    {
      key: "allowances",
      title: "allowances",
      format: "currency",
      currency: () => BASE_CURRENCY,
    },
    { key: "totalAbsenceDays", title: "absence Days" },
    {
      key: "totalAbsenceAmount",
      title: "absence Amount",
      format: "currency",
      currency: () => BASE_CURRENCY,
    },
    {
      key: "netSalaries",
      title: "net Salaries",
      format: "currency",
      currency: () => BASE_CURRENCY,
    },
    // {
    //   key: "taxes",
    //   title: "taxes",
    //   format: "currency",
    //   currency: () => BASE_CURRENCY,
    // },
    {
      key: "netAmout",
      title: "net Amout",
      format: "currency",
      currency: () => BASE_CURRENCY,
    },
    {
      key: "status",
      title: "approval stage",
      compute: (row: any) => row,
      formatter: (row: any) => <PayrollStageTracker status={row.status} compact />,
    },
    { key: "date", title: "date", format: "date" },
    // { key: "createdAt", title: "@", format: "date" },
  ];

  const filters: Filterable<Payroll>[] = [
    {
      key: "branchId",
      title: "branch",
      format: "select",
      data: [
        ...fetchBranches?.data?.items?.map((d) => ({
          id: d.id,
          name: d.location,
        })),
      ],
    },
    {
      key: "forMonth",
      title: "Payroll Month",
      format: "month",
      // operation: Operation.like,
    },
  ];

  const actions: Action[] = [
    {
      key: "",
      actionType: "badge",
      click: (d: Payroll) => {
        // MySwal.fire({
        //   showConfirmButton: false,
        //   allowOutsideClick: false,
        //   showCloseButton: true,
        //   width: 1500,
        //   html: <PayrollDetails paryollId={d.id} />,
        // });

        navigate(`/payrolls/details/${d.id}`);
      },
      title: "Details",
      color: "info",
    },
    // Approval chain: only the actions the signed-in user may take on each payroll are shown (decided by the API).
    {
      key: "",
      actionType: "badge",
      click: (d: Payroll) => confirmAdvancePayroll(payrollRef(d), refresh),
      title: "Check",
      color: "success",
      hide: (d: Payroll) => !d.actions?.canAdvance || d.actions.advanceLabel !== "Mark as checked",
    },
    {
      key: "",
      actionType: "badge",
      click: (d: Payroll) => confirmAdvancePayroll(payrollRef(d), refresh),
      title: "Approve",
      color: "success",
      hide: (d: Payroll) =>
        !d.actions?.canAdvance || d.actions.advanceLabel === "Mark as checked" || d.actions.advanceLabel === "Resubmit",
    },
    {
      key: "",
      actionType: "badge",
      click: (d: Payroll) => confirmAdvancePayroll(payrollRef(d), refresh),
      title: "Resubmit",
      color: "success",
      hide: (d: Payroll) => !d.actions?.canAdvance || d.actions.advanceLabel !== "Resubmit",
    },
    {
      key: "",
      actionType: "badge",
      click: (d: Payroll) => confirmReturnPayroll(payrollRef(d), refresh),
      title: "Return",
      color: "danger",
      hide: (d: Payroll) => !d.actions?.canReturnToHR && !d.actions?.canReturnToFinance,
    },
    {
      key: "",
      actionType: "badge",
      click: (d: Payroll) => showApprovals(d.id),
      title: "Approvals",
      color: "primary",
    },
    {
      key: "",
      actionType: "badge",
      click: async (d: Payroll) => {
        Swal.fire({
          title: "Are you sure you want to roll back this payroll?",
          text: "By confirming roll back, it means that this process is irrevocable, and the payroll will be permanently deleted.",
          icon: "warning",
          allowEscapeKey: false,
          allowOutsideClick: false,
          showCancelButton: true,
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#d33",
          confirmButtonText: "Yes, roll back!",
          showLoaderOnConfirm: true,
          preConfirm: async () => {
            const res = await httpService(ENDPIONTS.payrolls).delete(d.id);
            if (res.status === 204) {
              setReload((prev) => (prev === undefined ? true : !prev));
              Swal.fire("Done!", "Payroll has been removed.", "success");
            } else {
              Swal.fire("Oops!", "An error occurred, please try again later.", "error");
            }
          },
        });
      },
      title: "Roll Back",
      color: "danger",
      // Only while the payroll is with HR (initiated or returned).
      hide: (d: Payroll) => !d.actions?.canRollBack,
    },
  ];

  const handlePageChange = (page: number, size: number) => {
    queryStrings.set("size", size.toString());
    queryStrings.set("page", (page - 1).toString());

    navigate({ search: `?${queryStrings.toString()}` });
  };

  return (
    <div className="content container-fluid">
      <div className="row align-items-center">
        <div className="col">
          <h3 className="page-title">Payroll Report</h3>
        </div>
        <div className="col-auto float-end ms-auto">
          <TableVerticalConfigs filters={filters} />
        </div>
      </div>
      {awaiting.length > 0 && (
        <div className="awaiting-banner mt-4">
          <div className="awaiting-banner-icon">
            <i className="fa fa-bell" />
          </div>
          <div className="awaiting-banner-body">
            <strong>
              {awaiting.length} payroll{awaiting.length > 1 ? "s are" : " is"} waiting for your decision
            </strong>
            <div className="awaiting-chips">
              {awaiting.map((a) => (
                <button key={a.id} type="button" className="awaiting-chip" onClick={() => showApprovals(a.id)}>
                  {a.branch} · {a.forMonth}
                  <span>{PAYROLL_STATUS_LABELS[a.status] ?? a.status}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
      <div className="col-12 d-flex mt-4">
        <div className="card profile-box flex-fill">
          <div className="card-body">
            <Table
              actions={actions}
              class="table table-striped table-hover mb-0"
              data={fetchPayrolls.data ?? []}
              headers={headers}
              isFetchingPage={fetchPayrolls.isFetching}
              showCounter
              onPageChange={handlePageChange}
              paginationClass="row mt-3"
              //   rowModifier={rowModifier}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ListPayroll;
